import "./authEntrance.css";

/**
 * Hand-off from the sign-in page to the first signed-in page: the login marks
 * the moment it leaves, and the app shell, if it mounts shortly after, builds
 * itself up (sidebar from the left, header from above, content from below)
 * instead of appearing at once. Module state, not the router's location
 * state — a reload of /chatbot must not replay the entrance.
 */
const WINDOW_MS = 5000;
const ENTRANCE_MS = 1400;
const BODY_CLASS = "auth-entrance";

let markedAt = 0;

export const markAuthEntrance = () => {
  markedAt = Date.now();
};

/**
 * Plays the entrance once if the login marked it just before, and returns the
 * cleanup that ends it. The class stays on until that cleanup — i.e. until the
 * route changes — and is not taken off on a timer: the page content carries
 * its own fade-in (`animate-in`), which the entrance overrides, and removing
 * the class mid-page would start that fade a second time (the content blinked
 * out and in again ~1.5 s after sign-in). Safe to call twice in a row (React
 * strict mode): the mark is cleared on a timer, not by the cleanup.
 */
export const playAuthEntrance = (): (() => void) | undefined => {
  if (!markedAt || Date.now() - markedAt > WINDOW_MS) return undefined;
  const body = document.body;
  body.classList.add(BODY_CLASS);
  const timer = setTimeout(() => {
    markedAt = 0;
  }, ENTRANCE_MS);
  return () => {
    clearTimeout(timer);
    body.classList.remove(BODY_CLASS);
  };
};

/**
 * Login ↔ sign-up: the page being left marks the switch, the page arriving
 * slides its first step in. Module state again — the router's history state
 * survives a reload, and a reload must show the page at once.
 */
let siblingMarkedAt = 0;

export const markSiblingNavigation = () => {
  siblingMarkedAt = Date.now();
};

/** True right after `markSiblingNavigation`; read once per page mount. */
export const cameFromSibling = () => Date.now() - siblingMarkedAt < 1500;
